import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  ViewChild
} from '@angular/core';
import { EventType, MatrixEvent } from 'matrix-js-sdk';
import {
  MatCard,
  MatCardAvatar,
  MatCardContent,
  MatCardFooter,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle,
  MatCardTitleGroup
} from '@angular/material/card';
import { ReactiveFormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { TypingService } from '../../../services/typing/typing.service';
import { exhaustMap, from, Observable, Subject } from 'rxjs';
import { MatDivider } from '@angular/material/divider';
import { SbTypingComponent } from '../sb-typing/sb-typing.component';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { SbRoomSidebarComponent } from '../sb-room-sidebar/sb-room-sidebar.component';
import { MatIconButton } from '@angular/material/button';
import { UserService } from '../../../services/user/user.service';
import { RoomStateEvent } from 'matrix-js-sdk/lib/models/room-state';
import { Router, RouterOutlet } from '@angular/router';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';
import { SbMessageStreamComponent } from './sb-message-stream/sb-message-stream.component';
import { MatrixService } from '../../../services/matrix/matrix.service';
import { MediaService } from '../../../services/media/media.service';
import { ChatMessageService } from '../../../services/chat-message/chat-message.service';
import { RoomMember } from '../../../model/room-member';
import { ChatStoreService } from '../../../services/chat-store/chat-store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReceiptService } from '../../../services/receiptService/receipt.service';
import { SessionService } from '../../../services/session/session.service';
import { RoomHelperService } from '../../../services/room/room-helper.service';
import { RoomListAccessService } from '../../../services/room-list/room-list-access.service';
import { RoomDetailsTestIds } from './sb-room-details.test-ids';
import { RoomMembershipState } from '../../../../shared-api/roomMembershipState';
import { TiptapEditor } from '../tiptap-editor/tiptap-editor.component';

@Component({
  selector: 'app-room-details',
  imports: [
    MatCard,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltip,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardFooter,
    MatDivider,
    MatCardContent,
    SbTypingComponent,
    MatDrawer,
    MatDrawerContent,
    MatDrawerContainer,
    SbRoomSidebarComponent,
    MatCardTitleGroup,
    MatIconButton,
    AsyncPipe,
    SbMessageStreamComponent,
    MatCardAvatar,
    RouterOutlet,
    TiptapEditor
  ],
  templateUrl: './sb-room-details.component.html',
  styleUrl: './sb-room-details.component.scss'
})
/**
 * Komponente zur Anzeige aller Nachrichten im gegebenen Raum.
 * Zusätzlich besteht die Möglichkeit, weitere Nachrichten zu schreiben.
 * Die Nachrichtenliste wird über den {@link ChatMessageService}
 * verwaltet und automatisch aktualisiert,
 * wenn neue Nachrichten im Raum eintreffen.
 *
 * Zusätzlich werden Buttons zur Durchführung folgender Vorgänge angezeigt:
 * * Einladung annehmen
 * * Einladung ablehnen / Raum verlassen
 * * Matrix-ID einladen
 *
 * Hierdurch erfüllte AFO`s
 *
 * A_26221 - Private Read Receipts
 *
 * A_25431 - Eingabebenachrichtigungen
 */
export class SbRoomDetailsComponent implements OnInit, AfterViewInit {
  @ViewChild(TiptapEditor) tiptapEditor!: TiptapEditor;
  @ViewChild('container_chat') chatContainer!: ElementRef;
  protected readonly testIds = RoomDetailsTestIds;
  protected readonly typingUsers$: Observable<string[]>;
  protected roomId = input<string>();
  protected readonly matrix = inject(MatrixService);
  protected readonly typingService = inject(TypingService);
  protected readonly chatMessageService = inject(ChatMessageService);
  protected readonly roomListAccessService = inject(RoomListAccessService);
  protected readonly userService = inject(UserService);
  protected readonly sessionService = inject(SessionService);
  protected readonly mediaService = inject(MediaService);
  protected readonly receiptService = inject(ReceiptService);
  protected isRoomInfoSidebarOpen = true;
  protected readMarkerId: string | undefined;
  protected readonly roomHelperService = inject(RoomHelperService);
  private readonly chatStoreService = inject(ChatStoreService);
  private readonly router: Router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private readonly scrollTrigger$ = new Subject<void>();

  constructor() {
    this.typingUsers$ = this.typingService.typingUsers$;

    effect(
      async () => {
        const roomId = this.roomId();
        if (roomId) {
          // Zentrale Raumänderung => SessionService => ChatStoreService
          this.sessionService.setSessionRoomId(roomId);
          this.readMarkerId = this.receiptService.getReadMarker(roomId);
        }
      },
      { injector: this.injector }
    );
  }

  /**
   * Effect ist eine Methode, die jedes Mal gefeuert wird, wenn sich der Input der Klasse ändert.
   * Hier wird daraufhin die RoomId gesetzt und die Nachrichtenanzeige aktualisiert
   */
  ngOnInit(): void {
    this.scrollTrigger$
      .pipe(
        exhaustMap(() => from(this.takeNext())),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    const roomId = this.roomId();
    if (!roomId) {
      this.router.navigate(['/rooms']);
    } else {
      this.activateListener();
    }
  }

  /**
   * Nachdem der View erstellt wurde, soll, falls das Scroll-to-Bottom-Flag gesetzt ist, zum Anfang des Chats
   * gescrollt werden
   */
  ngAfterViewInit(): void {
    this.tiptapEditor?.messageSent.subscribe(() => document.getElementById('scrollLast')!.scrollIntoView());
  }

  /**
   * Falls der Scrollbar nur noch 100px von der Höhe des Messagecontainers entfernt ist,
   * dann sollen weitere Events in die Nachrichtenanzeige geladen werden.
   */
  public async scroll(event: Event): Promise<void> {
    const container = event.target as HTMLElement;
    if (container) {
      // Hier wird die Distanz der akutellen Scrollbar (contaioner.scrollHeight + container.scrollTop) zu meiner
      // maximalen Container höhe (container.clientHeight) verprobt und ob diese differenz kleiner als 100px ist
      if (container.scrollHeight + container.scrollTop - container.clientHeight < 100) {
        this.scrollTrigger$.next();
      }
    }
  }

  /**
   * Nach eigenem Nutzer gefilterte Nutzerliste des Raumes
   */
  public getOtherRoomMembers(members: RoomMember[] | null | undefined): string | undefined {
    if (!members) {
      return '';
    }
    return members
      .filter(
        member => this.matrix.client.getUserId() !== member.mxid && member.membershipState === RoomMembershipState.Join
      )
      .map(m => m.rawDisplayName)
      .join(', ');
  }

  /** @deprecated */
  public activateListener(): void {
    this.matrix.client.on(RoomStateEvent.Events, event => {
      if (event.getType() === EventType.RoomAvatar) {
        setTimeout(async () => {
          await this.userService.updateRoomData(event.getRoomId());
        }, 1000);
      }
    });

    this.matrix.client.on(RoomStateEvent.Members, (event: MatrixEvent) => {
      setTimeout(async () => {
        await this.userService.memberEventHandler(event);
      }, 1000);
    });
  }

  openDialog(): void {
    if (this.isRoomMember()) {
      this.router.navigate(['rooms', this.roomId(), 'settings']);
    }
  }

  protected toggleRoomInfoSidebar(): void {
    this.isRoomInfoSidebarOpen = !this.isRoomInfoSidebarOpen;
  }

  /**
   * Hole die vorherigen 50 MatrixEvents (beim Hochscrollen).
   * Lädt aus dem Client-Store die nächsten Nachrichten in die Nachrichten-Anzeige. Falls im Store nicht mehr genug
   * Nachrichten vorhanden sind, wird versucht, aus dem Backend neue Nachrichten zu laden.
   */
  private async takeNext(): Promise<void> {
    const roomId = this.roomId();

    if (!roomId) {
      throw new Error('Keine Raum-ID');
    }

    const client = this.matrix.client;

    // Hole Raum.
    const room = client.getRoom(roomId);
    if (!room) {
      throw new Error(`Raum mit ID ${roomId} nicht gefunden`);
    }

    // Hole aktuelle Timeline.
    const liveTimeline = room.getLiveTimeline();

    await client.paginateEventTimeline(liveTimeline, {
      backwards: true,
      limit: 50
    });

    // Konstruiere aus Events einen neuen ChatRoom und ersetze mit diesem den bestehenden ChatRoom im ChatStore
    const chatRoom = this.chatStoreService.roomEventsToChatRoom(liveTimeline.getEvents());
    if (!chatRoom) {
      throw new Error('Kein ChatRoom aus Timeline-Events erezeugbar');
    }
    this.chatStoreService.exchangeRoom(room.roomId, chatRoom);
  }

  private isRoomMember(): boolean {
    const roomId = this.roomId();

    if (!roomId) return false;

    return this.roomHelperService.getRoomMembership(roomId) === KnownMembership.Join;
  }
}
